import { t as createQrSizeMeasurer } from "../../qr-size-measurer-CXJfSfdn.js";
import { intrinsicsFromProjection, solveQrPose } from "./qr-pose.js";
//#region ../src/ar/qr/qr-derived-pose.ts
/**
* Replay a marker's observation history through a fresh size measurer (each
* observation sized against its own as-of depth context) and return the running
* median size in meters, or `null` if no observation could be sized yet.
*
* Re-runs the whole history per call — O(history), bounded by the slice ring
* buffer. Cheap for v1; memoize if the debug-viz cadence makes it hot.
*/
function deriveQrSizeM(text, observations, resolveDepthAt, sizeOptions) {
	if (observations.length === 0) return null;
	const measurer = createQrSizeMeasurer(sizeOptions);
	for (const o of observations) {
		const ctx = resolveDepthAt(o.timestamp);
		if (!ctx) continue;
		measurer.measure(text, o.corners, {
			width: o.imageWidth,
			height: o.imageHeight
		}, ctx);
	}
	return measurer.current(text).estimateM;
}
/**
* Solve the world pose of ONE raw observation given a known metric size. Pure
* (no depth): intrinsics come from the observation's projection + frame size,
* the corners + camera pose are recorded. Returns `null` when PnP rejects the
* quad or exceeds the reprojection gate.
*/
function solveQrPoseFromObservation(observation, sizeM, solver, maxReprojectionErrorPx) {
	const intrinsics = intrinsicsFromProjection(observation.projectionMatrix, observation.imageWidth, observation.imageHeight);
	const solution = solveQrPose({
		imagePoints: observation.corners,
		sizeM,
		intrinsics,
		cameraPose: observation.cameraPose,
		solver,
		...maxReprojectionErrorPx !== void 0 ? { maxReprojectionErrorPx } : {}
	});
	return solution ? solution.qrPoseWorld : null;
}
/**
* Derive the best-effort {@link DerivedQrPlacement} for a marker: accumulate the
* size across the whole observation history (each against its as-of depth), then
* solve the LATEST observation with that size — returning BOTH the pose and the
* size (the debug cube needs the size; deriving once avoids two history passes).
* Returns `null` when there is no history, no observation can be sized, or PnP
* rejects the latest quad — callers render nothing (best-effort viz) rather than
* throwing.
*/
function deriveQrPlacement(text, observations, deps) {
	if (observations.length === 0) return null;
	const sizeM = deriveQrSizeM(text, observations, deps.resolveDepthAt, deps.sizeOptions);
	if (sizeM === null) return null;
	const latest = observations[observations.length - 1];
	const pose = solveQrPoseFromObservation(latest, sizeM, deps.solver, deps.maxReprojectionErrorPx);
	return pose ? {
		pose,
		sizeM
	} : null;
}
/**
* Derive only the best-effort solved WORLD pose for a marker (the size is an
* internal of {@link deriveQrPlacement}). Returns `null` under the same
* conditions. Kept for callers that need the pose alone.
*/
function deriveSolvedQrPose(text, observations, deps) {
	return deriveQrPlacement(text, observations, deps)?.pose ?? null;
}
function createIncrementalQrPlacement(deps) {
	const measurer = createQrSizeMeasurer(deps.sizeOptions);
	const lastFoldedTs = /* @__PURE__ */ new Map();
	const lastPlacement = /* @__PURE__ */ new Map();
	return {
		update(text, observations) {
			if (observations.length === 0) return null;
			const newestTs = observations[observations.length - 1].timestamp;
			const foldedTs = lastFoldedTs.get(text) ?? -Infinity;
			if (newestTs <= foldedTs) return lastPlacement.get(text) ?? null;
			let maxFoldedTs = foldedTs;
			for (const o of observations) {
				if (o.timestamp <= foldedTs) continue;
				const ctx = deps.resolveDepthAt(o.timestamp);
				if (!ctx) continue;
				measurer.measure(text, o.corners, {
					width: o.imageWidth,
					height: o.imageHeight
				}, ctx);
				maxFoldedTs = o.timestamp;
			}
			lastFoldedTs.set(text, maxFoldedTs);
			const sizeM = measurer.current(text).estimateM;
			let placement = null;
			if (sizeM !== null) {
				const latest = observations[observations.length - 1];
				const pose = solveQrPoseFromObservation(latest, sizeM, deps.solver, deps.maxReprojectionErrorPx);
				placement = pose ? {
					pose,
					sizeM
				} : null;
			}
			lastPlacement.set(text, placement);
			return placement;
		},
		reset(text) {
			lastFoldedTs.delete(text);
			lastPlacement.delete(text);
			measurer.reset(text);
		}
	};
}
//#endregion
export { createIncrementalQrPlacement, deriveQrPlacement, deriveQrSizeM, deriveSolvedQrPose, solveQrPoseFromObservation };
